import * as React from 'react';
import * as Yup from 'yup';
import { Button, Grid, Link, Typography } from '@mui/material';
import { Form } from 'formik';
import { MuiForm } from 'lib/Form';
import { SelectFormField } from 'lib/form/fields/SelectFormField';
import { MuiFormLabel } from 'lib/form/MuiFormLabel';
import { Option } from 'lib/types';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { getActionTypeOptions } from 'components/actions/utils';
import {
    CLIENT_PLATFORM_OPTIONS,
    ORDER_PAYMENT_TYPE_OPTIONS,
    EActionTypeGroup,
    EChannel,
    EClientPlatform,
    ETimeOfDay,
    getActionTypeGroup
} from 'components/audience/models/addTriggersModel';
import {
    renderAmountField,
    renderChannelSelect,
    renderDayOfWeekSelect,
    ExpandableDynamicChipInput,
    renderDynamicMultiSelect,
    renderDynamicSingleSelect,
    renderDynamicTextField,
    renderMonthOfYearSelect,
    renderPrimaryPlatformSelect,
    renderScenarioSelect,
    renderTimeOfDaySelect,
    renderWeekOfYearInput,
    renderDynamicNumberField,
    renderDynamicDaysOfWeekSelect,
    renderDynamicDateRangeSelect
} from 'components/audience/utils/addTriggerUtils';
import { ActionType } from 'components/actions/models/Action';
import { EPrimaryPlatform } from 'components/customers/models/Customer';
import { OrderScenario } from 'components/order/model/Order';
import { ScheduledTaskType } from 'components/scheduled-tasks/scheduledTasks.types';
import { parseRelativeTimeString, validateRelativeTimeString } from 'lib/relativeTimeString';
import { NumberFormField } from 'lib/form/fields/NumberFormField';
import { EPaymentProvider } from 'components/settings/PaymentsSettings';
import { isString } from 'lib/typeguards';
import { useSelector } from 'react-redux';
import { ApplicationState } from 'store/store';
import { MARKETING_VOUCHERS } from 'config/routes';
import { AllConditionPredicates } from 'components/rules/rules.types';

export interface RuleScheduledTaskNotificationTargetFormData {
    type: ScheduledTaskType.CREATE_PUSH_NOTIFICATION;
    triggerAt: string;
    tag?: string;
    actionType?: ActionType;
    locationIds?: string[];
    dayPart?: ETimeOfDay;
    dayOfWeek?: string;
    weekOfYear?: string;
    monthOfYear?: string;
    primaryPlatform?: EPrimaryPlatform;
    amountPredicate?: AllConditionPredicates;
    amount?: string;
    scenario?: OrderScenario;
    channel?: EChannel;
    audienceIds?: string[];
    schemeId?: string;
    perkId?: string;
    voucherId?: string;
    clientPlatforms?: string[];
    categoryIds?: string[];
    productIds?: string[];
    paymentMethod?: string;
    emailEndsWith?: string;
    arrivalDaysOfWeek?: string[];
    arrivalDateFrom?: Date;
    arrivalDateTo?: Date;
    departureDaysOfWeek?: string[];
    departureDateFrom?: Date;
    departureDateTo?: Date;
    favouriteLocationIds?: string[];
}

export interface RuleScheduledTaskNotificationTargetInternalFormData {
    triggerAtOffset: number;
    triggerAtUnit: string;
}

interface RuleScheduledTaskNotificationTargetFormProps {
    open: boolean;
    onNext: (data: RuleScheduledTaskNotificationTargetFormData) => void;
    onCancel: () => void;
    initialData?: Partial<RuleScheduledTaskNotificationTargetFormData>;
    locationOptions: Option[];
    audienceOptions: Option[];
    adjustmentSchemeOptions: Option[];
    perkOptions: Option[];
}

const validationSchema = Yup.object().shape({
    actionType: Yup.string().typeError(' ').required(' '),
    audienceIds: Yup.array().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.SIXTH,
        then: Yup.array().min(1, 'At least one audience is required')
    }),
    schemeId: Yup.string().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.SEVENTH,
        then: Yup.string().required(' ')
    }),
    perkId: Yup.string().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType &&
            (getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH ||
                getActionTypeGroup(actionType) === EActionTypeGroup.TENTH),
        then: Yup.string().required(' ')
    }),
    voucherId: Yup.string().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.NINTH,
        then: Yup.string().required('Please enter a voucher ID')
    }),
    locationIds: Yup.array().optional(),
    triggerAtOffset: Yup.number().required(' ').min(1, 'Delay must be at least 1'),
    triggerAtUnit: Yup.string().required(' ').oneOf(['minutes', 'hours', 'days', 'weeks']),
    arrivalDateFrom: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    }),
    arrivalDateTo: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    }),
    departureDateFrom: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    }),
    departureDateTo: Yup.mixed().when('actionType', {
        is: (actionType: ActionType) =>
            !!actionType && getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH,
        then: Yup.mixed().notRequired()
    })
});

const relativeTimeOptions: Option[] = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
    { value: 'weeks', label: 'Weeks' }
];

const typeOptions: Option[] = [
    {
        value: ScheduledTaskType.CREATE_PUSH_NOTIFICATION,
        label: 'Push Notification'
    }
];

export const RuleScheduledTaskNotificationTargetForm: React.FC<
    RuleScheduledTaskNotificationTargetFormProps
> = ({
    open,
    onNext,
    onCancel,
    initialData,
    locationOptions,
    audienceOptions,
    adjustmentSchemeOptions,
    perkOptions
}) => {
    const { paymentProvider } = useSelector((state: ApplicationState) => state?.settings?.settings);
    const isStripe = React.useMemo(
        () => isString(paymentProvider) && paymentProvider === EPaymentProvider.Stripe,
        [paymentProvider]
    );
    const computedClientPlatformOptions = React.useMemo(() => {
        if (!isStripe) {
            return CLIENT_PLATFORM_OPTIONS.filter(option => option.value !== EClientPlatform.TERMINAL);
        }
        return CLIENT_PLATFORM_OPTIONS;
    }, [isStripe]);
    const initialValues = React.useMemo<
        RuleScheduledTaskNotificationTargetFormData & RuleScheduledTaskNotificationTargetInternalFormData
    >(() => {
        let offset, unit;
        if (initialData?.triggerAt && validateRelativeTimeString(initialData.triggerAt)) {
            const parsed = parseRelativeTimeString(initialData.triggerAt);
            offset = parseInt(parsed.amountString);
            unit = parsed.unit;
        }
        return {
            ...initialData,
            type: ScheduledTaskType.CREATE_PUSH_NOTIFICATION,
            triggerAt: initialData?.triggerAt || null,
            triggerAtUnit: unit || 'minutes',
            triggerAtOffset: offset,
            actionType: initialData?.actionType || null,
            locationIds: initialData?.locationIds || [],
            favouriteLocationIds: initialData?.favouriteLocationIds || [],
            audienceIds: initialData?.audienceIds || [],
            clientPlatforms: initialData?.clientPlatforms || [],
            categoryIds: initialData?.categoryIds || [],
            productIds: initialData?.productIds || [],
            departureDaysOfWeek: initialData?.departureDaysOfWeek || [],
            arrivalDaysOfWeek: initialData?.arrivalDaysOfWeek || [],
            arrivalDateFrom: initialData.arrivalDateFrom || null,
            arrivalDateTo: initialData.arrivalDateTo || null,
            departureDateFrom: initialData.departureDateFrom || null,
            departureDateTo: initialData.departureDateTo || null
        };
    }, [initialData]);

    const actionTypeOptions = getActionTypeOptions({ includeNoneOption: false });

    const handleSubmit = React.useCallback(
        (
            values: RuleScheduledTaskNotificationTargetFormData &
                RuleScheduledTaskNotificationTargetInternalFormData
        ) => {
            onNext({
                ...values,
                triggerAt: `+${values.triggerAtOffset}${values.triggerAtUnit}`
            } as RuleScheduledTaskNotificationTargetFormData);
        },
        [onNext]
    );

    const voucherIdDescription = (
        <Typography variant="caption">
            To view and copy your voucher ID, click{' '}
            <Link href={MARKETING_VOUCHERS} target="_blank">
                here
            </Link>{' '}
            to open your vouchers in a new window.
        </Typography>
    );
    if (!open) {
        return null;
    }
    return (
        <MuiForm
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnMount
        >
            {({ submitForm, isSubmitting, values: { actionType }, isValid }) => (
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <SelectFormField
                                name="type"
                                options={typeOptions}
                                label={<MuiFormLabel required>Type</MuiFormLabel>}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Grid container alignItems="center">
                                <SelectFormField
                                    label={<MuiFormLabel required>Action Type</MuiFormLabel>}
                                    options={actionTypeOptions}
                                    name="actionType"
                                />
                            </Grid>
                        </Grid>
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SIXTH &&
                            renderDynamicMultiSelect('audienceIds', 'Audiences', audienceOptions, true)}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SEVENTH &&
                            renderDynamicSingleSelect(
                                'schemeId',
                                'Adjustment Scheme',
                                adjustmentSchemeOptions,
                                true
                            )}
                        {(getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH ||
                            getActionTypeGroup(actionType) === EActionTypeGroup.TENTH) && (
                            <>{renderDynamicSingleSelect('perkId', 'Perk', perkOptions, true)}</>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.NINTH && (
                            <>
                                {renderDynamicTextField(
                                    'voucherId',
                                    'Voucher ID',
                                    true,
                                    '',
                                    voucherIdDescription
                                )}
                            </>
                        )}
                        <Grid item xs={12}>
                            <Grid container alignItems="center">
                                <Grid item xs={4}>
                                    <MuiFormLabel required>Delay By</MuiFormLabel>
                                </Grid>
                                <Grid item xs={8}>
                                    <Grid container spacing={2} alignItems="flex-start">
                                        <Grid item xs={6}>
                                            <NumberFormField min={1} required name="triggerAtOffset" />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <SelectFormField
                                                native={false}
                                                options={relativeTimeOptions}
                                                name="triggerAtUnit"
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                        {getActionTypeGroup(actionType) === EActionTypeGroup.ELEVENTH && (
                            <>
                                <Grid item xs={12}>
                                    <Typography variant="body1">Basket contains:</Typography>
                                </Grid>
                                <ExpandableDynamicChipInput
                                    name="categoryIds"
                                    label="Category ID(s)"
                                    placeholder="e.g. 5, 6, 7"
                                    isRequired={false}
                                    isExpandable
                                />
                                <ExpandableDynamicChipInput
                                    name="productIds"
                                    label="Product ID(s)"
                                    placeholder="e.g. 100, 101, 102"
                                    isRequired={false}
                                    isExpandable
                                />
                                {renderDynamicSingleSelect(
                                    'paymentMethod',
                                    'Payment Method',
                                    ORDER_PAYMENT_TYPE_OPTIONS,
                                    false
                                )}
                            </>
                        )}
                        {renderDynamicMultiSelect(
                            'clientPlatforms',
                            'Channel',
                            computedClientPlatformOptions,
                            false
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FIRST && (
                            <>
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.SECOND && (
                            <>
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.THIRD && (
                            <>
                                {renderAmountField()}
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                                {renderScenarioSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FOURTH && (
                            <>
                                {renderAmountField()}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                                {renderChannelSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FIFTH && (
                            <>
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {(getActionTypeGroup(actionType) === EActionTypeGroup.EIGHTH ||
                            getActionTypeGroup(actionType) === EActionTypeGroup.NINTH) && (
                            <>
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.TENTH && (
                            <>
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.ELEVENTH && (
                            <>
                                {renderAmountField()}
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                                {renderScenarioSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.TWELFTH && (
                            <>
                                {renderDynamicTextField(
                                    'emailEndsWith',
                                    'Email ends with',
                                    false,
                                    'e.g. ac.uk'
                                )}
                                {renderTimeOfDaySelect()}
                                {renderDayOfWeekSelect()}
                                {renderWeekOfYearInput()}
                                {renderMonthOfYearSelect()}
                                {renderPrimaryPlatformSelect()}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.THIRTEENTH && (
                            <>
                                {renderDynamicMultiSelect('locationIds', 'Locations', locationOptions, false)}
                                {renderDynamicDateRangeSelect(
                                    'arrivalDateFrom',
                                    'arrivalDateTo',
                                    'Arrival date'
                                )}
                                {renderDynamicDateRangeSelect(
                                    'departureDateFrom',
                                    'departureDateTo',
                                    'Departure date'
                                )}
                                {renderDynamicDaysOfWeekSelect('arrivalDaysOfWeek', 'Arrival day')}
                                {renderDynamicDaysOfWeekSelect('departureDaysOfWeek', 'Departure day')}
                                {renderDynamicNumberField(
                                    'stayLength',
                                    'stayLengthPredicate',
                                    'Stay length (nights)'
                                )}
                                {renderDynamicTextField('status', 'Booking status', false, '')}
                                {renderDynamicTextField(
                                    'bookerEmailEndsWith',
                                    'Booker email ends with',
                                    false,
                                    'e.g. ac.uk'
                                )}
                                {renderDynamicNumberField(
                                    'guestsNumber',
                                    'guestsNumberPredicate',
                                    'Number of Guests'
                                )}
                                {/* TODO: waiting for platform to support this field */}
                                {/* {renderDynamicTextField(
                                    'guestEmailEndsWith',
                                    'Guest email ends with',
                                    false,
                                    'e.g. ac.uk'
                                )} */}
                                {renderDynamicTextField('rateCode', 'Rate/Package code', false, '')}
                                {renderDynamicNumberField('cost', 'costPredicate', 'Total cost')}
                            </>
                        )}
                        {getActionTypeGroup(actionType) === EActionTypeGroup.FOURTEENTH && (
                            <>
                                {renderDynamicMultiSelect(
                                    'favouriteLocationIds',
                                    'Favourite locations',
                                    locationOptions,
                                    false
                                )}
                            </>
                        )}

                        <Grid item xs={12}>
                            <TextFormField
                                name="tag"
                                description="Add a label to this notification to group it with similar ones."
                                label={<MuiFormLabel>Tag</MuiFormLabel>}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Row gutter align="flex-end" valign="center">
                                <Button variant="outlined" onClick={onCancel}>
                                    Cancel
                                </Button>
                                <LoadingButton
                                    variant="contained"
                                    color="primary"
                                    disabled={isSubmitting || !isValid}
                                    loading={isSubmitting}
                                    onClick={submitForm}
                                >
                                    Next
                                </LoadingButton>
                            </Row>
                        </Grid>
                    </Grid>
                </Form>
            )}
        </MuiForm>
    );
};
